var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { NON_ED25519 } from '@usecapsule/web-sdk';
import { FilledDisabledInput, FlexColumn, Heading, QRContainer, SecondaryText } from '../common.js';
import styled from 'styled-components';
import { CpslAlert, CpslButton, CpslIcon, CpslQrCode, CpslSpinner, CpslTab, CpslTabs } from '@usecapsule/react-components';
import { useCapsuleStore, useModalStore, useThemeStore } from '../../stores/index.js';
import { NETWORKS, ON_RAMP_PROVIDERS } from '../../constants/constants.js';
import { useEffect, useState } from 'react';
import { ModalStep } from '../../utils/steps.js';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard.js';
import { validateOnRampConfig } from '../../utils/validateOnRampConfig.js';
const TABS = {
    buy: 'Buy',
    receive: 'Receive',
};
function list(networks) {
    return networks.length === 1
        ? NETWORKS[networks[0]]
        : `${networks
            .map(id => NETWORKS[id])
            .slice(0, -1)
            .join(', ')}${networks.length > 2 ? ',' : ''} and ${NETWORKS[networks[networks.length - 1]]}`;
}
const OnRamp = ({ config, index }) => {
    const capsule = useCapsuleStore(state => state.capsule);
    const setStep = useModalStore(state => state.setStep);
    const setOnRampPurchase = useModalStore(state => state.setOnRampPurchase);
    const [isLoading, setIsLoading] = useState(false);
    const provider = config.providers[index];
    const { feeLower, feeUpper, methods, name, icon } = ON_RAMP_PROVIDERS[provider.id];
    const onClick = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        const newOnRampPurchase = yield capsule.createOnRampPurchase(provider.id, config.network, config.asset, config.testMode);
        setOnRampPurchase(newOnRampPurchase);
        setStep(ModalStep.ADD_FUNDS_AWAITING);
    });
    useEffect(() => {
        setOnRampPurchase(undefined);
    }, []);
    return (_jsx(OnRampButton, { fullWidth: true, variant: "secondary", onClick: onClick, children: _jsxs(OnRampButtonContainer, { children: [_jsxs(OnRampButtonUpper, { children: [_jsxs(OnRampName, { children: [icon, name] }), isLoading ? _jsx(CpslSpinner, { size: 24 }) : _jsx(RightArrowIcon, { icon: "arrow" })] }), _jsxs(OnRampButtonLower, { children: [_jsxs(OnRampStat, { children: ["Fee: ", feeUpper !== undefined ? `${feeLower.toFixed(2)}-${feeUpper.toFixed(2)}%` : `${feeLower.toFixed(2)}%`] }), _jsx(OnRampStat, { children: methods.map(m => m.toString()).join(', ') }), _jsxs(OnRampStat, { children: [_jsx(CpslIcon, { icon: "lightning" }), "Instant"] })] })] }) }));
};
export const AddFunds = ({ hasFinishedAnimation }) => {
    const [isCopied, copy] = useCopyToClipboard();
    const appName = useThemeStore(state => state.appName);
    const capsule = useCapsuleStore(state => state.capsule);
    const onRampConfig = useModalStore(state => state.onRampConfig);
    const networks = useModalStore(state => state.networks);
    const isOnRampConfigured = (onRampConfig === null || onRampConfig === void 0 ? void 0 : onRampConfig.providers.length) > 0;
    const [walletId] = useState(capsule.findWalletId(undefined, { scheme: NON_ED25519 }));
    const [tab, setTab] = useState(isOnRampConfigured ? 'buy' : 'receive');
    const [configError, setConfigError] = useState();
    const walletAddress = capsule.getDisplayAddress(walletId);
    const onSetTab = (event) => {
        setTab(event.detail.tab);
    };
    const onCopy = () => {
        copy(walletAddress);
    };
    useEffect(() => {
        try {
            validateOnRampConfig(onRampConfig);
            setConfigError(undefined);
        }
        catch (e) {
            setConfigError(e);
        }
    }, [onRampConfig]);
    return (_jsxs(_Fragment, { children: [_jsxs(FlexColumn, { style: { width: '100%' }, children: [_jsx(Heading, { style: { marginBottom: '12px' }, children: _jsx("span", { children: "Fund Your Wallet" }) }), isOnRampConfigured && (_jsx(TabsContainer, { children: _jsx(CpslTabs, { selectedTab: hasFinishedAnimation ? tab : '', onCpslTabsChanged: onSetTab, fullWidth: true, children: Object.entries(TABS).map(([tab, title]) => (_jsx(Tab, { tab: tab, children: title }, tab))) }) }))] }), _jsx(LowerContainer, { children: tab === 'buy' ? (configError ? (_jsx(CpslAlert, { children: _jsxs("div", { children: ["There was an on-ramp configuration error when instantiating this Capsule Modal:", _jsx("br", {}), _jsx("br", {}), _jsx("span", { style: { fontFamily: 'monospace' }, children: configError.toString().split(': ').pop() }), _jsx("br", {}), _jsx("br", {}), "If you are a user of ", appName, ", please contact support."] }) })) : (_jsxs(OnRampContainer, { children: [_jsx(SecondaryText, { children: _jsx("span", { children: "Choose a provider to fund your wallet." }) }), onRampConfig.providers.map((provider, index) => {
                            return _jsx(OnRamp, { config: onRampConfig, index: index }, provider.id);
                        })] }))) : (_jsxs(_Fragment, { children: [_jsx(SecondaryText, { children: _jsx("span", { children: "Scan with your phone's camera" }) }), _jsx(QRContainer, { children: _jsx(CpslQrCode, { url: walletAddress }) }), _jsx(SecondaryText, { children: _jsx("span", { children: "Or copy your wallet address" }) }), _jsx(AddressDisplay, { disabled: true, value: walletAddress, noAutoDisable: true, children: _jsx(CpslButton, { slot: "end", variant: "ghost", onClick: onCopy, children: _jsx(CpslIcon, { icon: isCopied ? 'check' : 'copy' }) }) }), _jsxs(NetworkAlert, { children: [_jsxs(NetworkAlertTitle, { children: [_jsx(NetworkAlertIcon, { icon: "alertCircle" }), _jsx("span", { children: "Supported Networks" })] }), _jsxs(NetworkAlertText, { children: ["Only assets on ", list(networks), " are supported by ", appName, "."] })] })] })) })] }));
};
const TabsContainer = styled.div `
  align-self: center;
  width: 100%;
`;
const LowerContainer = styled(FlexColumn) `
  width: 100%;

  & > * {
    width: 100%;
  }
`;
const Tab = styled(CpslTab) `
  width: 50%;
`;
const OnRampContainer = styled(FlexColumn) `
  gap: 12px;
`;
const OnRampButton = styled(CpslButton) `
  --button-padding-top: 16px;
  --button-padding-left: 16px;
  --button-padding-right: 16px;
  --button-padding-bottom: 16px;
  --button-box-shadow: none;
  --cpsl-color-secondary-button-border-default: var(--cpsl-color-background-16);
  --cpsl-color-secondary-button-surface-hover: var(--cpsl-color-foreground-96);
`;
const OnRampButtonContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;
const OnRampButtonUpper = styled.div `
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const RightArrowIcon = styled(CpslIcon) `
  --icon-color: var(--);
`;
const OnRampButtonLower = styled.div `
  display: flex;
  justify-content: space-between;
`;
const OnRampName = styled.div `
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
`;
const OnRampStat = styled.div `
  display: flex;
  gap: 2px;
  align-items: center;
  font-size: 12px;

  cpsl-icon {
    --height: 12px;
    --width: 12px;
  }
`;
const AddressDisplay = styled(FilledDisabledInput) `
  --container-background-color: var(--cpsl-color-background-0);
  --input-background-color: transparent;
`;
const NetworkAlert = styled.div `
  color: var(--cpsl-color-foreground-0) !important;
  background-color: var(--cpsl-color-foreground-96);
  border-color: var(--cpsl-color-background-16) !important;
  border-radius: var(--cpsl-border-radius-alert);
  border: 1px solid;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const NetworkAlertTitle = styled.div `
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  font-weight: 500;
`;
const NetworkAlertText = styled.div `
  font-size: 14px;
`;
const NetworkAlertIcon = styled(CpslIcon) `
  --width: 20px;
  --height: 20px;
`;
